<script setup lang="ts">
import { __ } from '@@/bits/intl'
import type { AutoplayDurationType } from '@@/bits/slideshow'
import { AUTOPLAY_DURATIONS } from '@@/bits/slideshow'
import OzOverlay from '@@/library/v4/components/OzOverlay.vue'
import OzPlainButton, {
  OzPlainButtonColorScheme,
  OzPlainButtonSizePreset,
} from '@@/library/v4/components/OzPlainButton.vue'
import { useWindowSizeStore } from '@@/pinia/window_size'
import AutoplayMenu from '@@/vuecomponents/AutoplayMenu.vue'
import { shiftUpWhenOverflow, useFloating } from '@@/vuecomposables/floating'
import { storeToRefs } from 'pinia'
import type { ComponentPublicInstance, CSSProperties } from 'vue'
import { computed, ref } from 'vue'

defineProps<{
  xAutoplayMenu: boolean
  isAutoplayLooping: boolean
  toggleLoopButtonText?: string
  selectedAutoplayDuration: AutoplayDurationType
  autoplayDurationInMs: number
  darkMode: boolean
  testIdPrefix?: string
  // Analytics - should be updated to use event tracker later instead of data-pepin-id
  durationTypeAnalyticsIdPrefix?: string
  loopAnalyticsId?: string
}>()

defineEmits<{
  (event: 'toggle-autoplay-menu'): void
  (event: 'toggle-loop'): void
  (event: 'select-duration-type', value: AutoplayDurationType): void
}>()

const { windowHeight } = storeToRefs(useWindowSizeStore())

const button = ref<InstanceType<typeof OzPlainButton> | null>(null)
const popover = ref<ComponentPublicInstance<HTMLElement> | null>(null)
const reference = computed(() => button.value?.$el as any)
const floating = computed(() => popover.value?.$el as any)

// The maximum popover height is the window height minus a 12px margin.
const MAX_POPOVER_HEIGHT = computed(() => windowHeight.value - 12 * 2)
const { floatingStyles } = useFloating(reference, floating, {
  middleware: [shiftUpWhenOverflow()],
  placement: 'top-start',
})

const referenceContainerStyles = computed<CSSProperties>(() => ({
  width: `${reference.value?.offsetWidth}px`,
  height: `${reference.value?.offsetHeight}px`,
}))
</script>

<template>
  <div class="flex items-center justify-center" :style="referenceContainerStyles">
    <div class="box-border" :style="{ position: 'absolute' }">
      <OzPlainButton
        ref="button"
        :aria-label="__('Open modal to select autoplay duration')"
        :title="__('Open modal to select autoplay duration')"
        :size-preset="OzPlainButtonSizePreset.H32px"
        :dark-mode="darkMode"
        :text="AUTOPLAY_DURATIONS[selectedAutoplayDuration].short"
        :color-scheme="OzPlainButtonColorScheme.Secondary"
        :data-testid="`${testIdPrefix}AutoplayMenuButton`"
        :data-duration="autoplayDurationInMs"
        @click="$emit('toggle-autoplay-menu')"
      />
      <OzOverlay
        v-if="xAutoplayMenu"
        :scrim="'popover'"
        :dark-mode="darkMode"
        @scrim-esc="$emit('toggle-autoplay-menu')"
        @scrim-click="$emit('toggle-autoplay-menu')"
      >
        <AutoplayMenu
          v-if="xAutoplayMenu"
          ref="popover"
          :style="{ ...floatingStyles, maxHeight: `${MAX_POPOVER_HEIGHT}px` }"
          :selected-autoplay-duration="selectedAutoplayDuration"
          :dark-mode="darkMode"
          :is-autoplay-looping="isAutoplayLooping"
          :toggle-loop-button-text="toggleLoopButtonText"
          :test-id-prefix="testIdPrefix"
          :duration-type-analytics-id-prefix="durationTypeAnalyticsIdPrefix"
          :loop-analytics-id="loopAnalyticsId"
          @select-duration-type="$emit('select-duration-type', $event)"
          @toggle-loop="$emit('toggle-loop')"
        />
      </OzOverlay>
    </div>
  </div>
</template>
